import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export default function CompanyPageSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <div className={cn("max-w-[1500px] mx-auto", className)}>
      {/* Header: avatar + name left, action buttons right */}
      <div className="flex items-center justify-between gap-4 pt-5 pb-3 md:pt-8 md:pb-4">
        <div className="flex items-center min-w-0 gap-3">
          <Skeleton className="w-24 h-24 rounded-full shrink-0" />
          <Skeleton className="w-48 h-8 sm:w-64 md:h-9" />
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Skeleton className="w-10 h-10 rounded-md" />
          <Skeleton className="w-10 h-10 rounded-md" />
          <Skeleton className="w-10 h-10 rounded-md" />
          <Skeleton className="w-10 h-10 rounded-md" />
          <Skeleton className="w-10 h-10 rounded-md" />
        </div>
      </div>

      {/* Tabs bar – centered */}
      <div className="flex justify-center mb-3">
        <div className="flex gap-1 p-1 rounded-lg bg-muted">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="w-16 h-7 rounded-md sm:w-20" />
          ))}
        </div>
      </div>

      {/* Insights content: 2-column grid */}
      <div className="grid grid-cols-1 gap-4 sm:gap-5 items-start @[1050px]:grid-cols-[2fr_1fr]">
        {/* Left column */}
        <div className="flex flex-col gap-5">
          {/* Activity card */}
          <Card>
            <CardHeader className="pb-3">
              <Skeleton className="w-28 h-5" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Skeleton className="w-full h-4" />
                <Skeleton className="w-full h-4" />
                <Skeleton className="w-3/4 h-4" />
              </div>
            </CardContent>
          </Card>

          {/* Financials summary card */}
          <Card>
            <CardHeader className="pb-3">
              <Skeleton className="w-32 h-5" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="w-20 h-3" />
                    <Skeleton className="w-24 h-6" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Employees suggested card */}
          <Card>
            <CardHeader className="pb-3">
              <Skeleton className="w-40 h-5" />
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="w-10 h-10 rounded-full shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="w-32 h-4" />
                      <Skeleton className="w-48 h-3" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* AI Advice card */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Skeleton className="w-5 h-5 rounded-full" />
                <Skeleton className="w-40 h-5" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Skeleton className="w-full h-4" />
                <Skeleton className="w-full h-4" />
                <Skeleton className="w-5/6 h-4" />
                <Skeleton className="w-2/3 h-4" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-5">
          {/* Summary card */}
          <Card>
            <CardHeader className="pb-3">
              <Skeleton className="w-24 h-5" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Skeleton className="w-4 h-4 shrink-0" />
                    <Skeleton className={cn("h-4", i % 2 === 0 ? "w-full" : "w-3/4")} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick actions */}
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <Skeleton className="w-24 h-4" />
                    <Skeleton className="w-5 h-5" />
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
